/** SEO copy for quest pages (shared by the Worker HTML shell and the web app). */

export type SeoLocale = "en" | "ko";

export type SeoMessageKey =
  | "page.docTitle"
  | "page.docTitleEmpty"
  | "explainer.title"
  | "explainer.what"
  | "explainer.howAnywhereNoLimit"
  | "explainer.howAnywhereLimit"
  | "explainer.howFixedNoLimit"
  | "explainer.howFixedLimit"
  | "explainer.tips"
  | "explainer.spot.title"
  | "explainer.spot.what"
  | "explainer.spot.howAnywhereNoLimit"
  | "explainer.spot.howAnywhereLimit"
  | "explainer.spot.howFixedNoLimit"
  | "explainer.spot.howFixedLimit"
  | "explainer.spot.tips"
  | "summary.placeRoute"
  | "summary.titleRoute"
  | "summary.placeSpots"
  | "summary.titleSpots"
  | "summary.spotTime"
  | "summary.walk"
  | "summary.endpoints"
  | "summary.byAuthor"
  | "summary.published"
  | "summary.untitled"
  | "home.description"
  | "home.intro";

const en: Record<SeoMessageKey, string> = {
  "page.docTitle": "{title} · Mapus",
  "page.docTitleEmpty": "Mapus",
  "explainer.title": "What is a route quest?",
  "explainer.what":
    "A route quest is a path drawn on the map like artwork. Follow that line on real streets and trails by running, walking, or cycling—your GPS track fills in the picture as you go.",
  "explainer.howAnywhereNoLimit":
    "Start anywhere on the route unless this quest sets fixed start and finish points. Follow the line on the ground; your match rate shows how closely you traced it. There is no time limit—go at your own pace.",
  "explainer.howAnywhereLimit":
    "Start anywhere on the route unless this quest sets fixed start and finish points. Follow the line on the ground; your match rate shows how closely you traced it. This quest has a {count}-minute time limit.",
  "explainer.howFixedNoLimit":
    "Start at the marked start and follow the line to the finish. Your match rate shows how closely you traced it. There is no time limit—go at your own pace.",
  "explainer.howFixedLimit":
    "Start at the marked start and follow the line to the finish. Your match rate shows how closely you traced it. This quest has a {count}-minute time limit.",
  "explainer.tips":
    "Stay aware of traffic and follow local laws. Be considerate of residents and private property. You can pause and resume whenever you need to.",
  "explainer.spot.title": "What is a spot quest?",
  "explainer.spot.what":
    "A spot quest is a set of places on the map to discover. Visit each spot in any order—read the story, see photos, and check in when you arrive.",
  "explainer.spot.howAnywhereNoLimit":
    "Open the quest and go to the spots on foot, by bike, or however you like. Mark each spot when you arrive. There is no set order and no time limit.",
  "explainer.spot.howAnywhereLimit":
    "Open the quest and go to the spots on foot, by bike, or however you like. Mark each spot when you arrive. There is no set order; this quest has a {count}-minute time limit.",
  "explainer.spot.howFixedNoLimit":
    "Begin at the marked start spot, then visit the other spots in any order. Mark each spot when you arrive. There is no time limit—go at your own pace.",
  "explainer.spot.howFixedLimit":
    "Begin at the marked start spot, then visit the other spots in any order. Mark each spot when you arrive. This quest has a {count}-minute time limit.",
  "explainer.spot.tips":
    "Stay aware of traffic and follow local laws. Be considerate of residents, businesses, and private property. You can pause and resume whenever you need to.",
  "summary.placeRoute": "A {distance} route quest around {place}.",
  "summary.titleRoute": "A {distance} route quest: {title}.",
  "summary.placeSpots": "A spot quest with {count} stops around {place}.",
  "summary.titleSpots": "A spot quest with {count} stops: {title}.",
  "summary.spotTime": "About {time} at the stops in total.",
  "summary.walk": "Walking takes about {time}.",
  "summary.endpoints": "It runs from {start} to {finish}.",
  "summary.byAuthor": "Created by {name}.",
  "summary.published": "Published {date}.",
  "summary.untitled": "Untitled",
  "home.description":
    "Mapus is a map where you discover route quests—paths drawn like artwork that you follow on foot, by bike, or on a run—and trace them with GPS.",
  "home.intro":
    "Browse quests on the map, open a route to see distance and area, then follow the line in the real world. Creators publish route quests; players match their track to the drawing.",
};

const ko: Record<SeoMessageKey, string> = {
  "page.docTitle": "{title} · Mapus",
  "page.docTitleEmpty": "Mapus",
  "explainer.title": "루트 퀘스트란?",
  "explainer.what":
    "루트 퀘스트는 지도 위에 선으로 그린 경로를, 실제 거리에서 걷거나 달리거나 자전거로 따라가는 퀘스트예요. GPS로 기록한 이동 경로가 그 선과 겹치면서 경로를 따라 채워 나가요.",
  "explainer.howAnywhereNoLimit":
    "출발 지점이 정해져 있지 않다면 경로 위 아무 곳에서 시작해 선을 따라가면 돼요. 얼마나 잘 맞췄는지 매치율로 확인할 수 있고, 제한 시간 없이 자신의 페이스로 즐기면 됩니다.",
  "explainer.howAnywhereLimit":
    "출발 지점이 정해져 있지 않다면 경로 위 아무 곳에서 시작해 선을 따라가면 돼요. 얼마나 잘 맞췄는지 매치율로 확인할 수 있고, 이 퀘스트는 {count}분 제한이 있어요.",
  "explainer.howFixedNoLimit":
    "표시된 출발 지점에서 시작해 선을 따라 도착 지점까지 가면 돼요. 얼마나 잘 맞췄는지 매치율로 확인할 수 있고, 제한 시간 없이 자신의 페이스로 즐기면 됩니다.",
  "explainer.howFixedLimit":
    "표시된 출발 지점에서 시작해 선을 따라 도착 지점까지 가면 돼요. 얼마나 잘 맞췄는지 매치율로 확인할 수 있고, 이 퀘스트는 {count}분 제한이 있어요.",
  "explainer.tips":
    "안전과 교통법규를 지키고, 주민과 사유지를 존중해 주세요. 필요할 때는 언제든 멈췄다가 이어서 진행해도 됩니다.",
  "explainer.spot.title": "스팟 퀘스트란?",
  "explainer.spot.what":
    "스팟 퀘스트는 지도에 표시된 장소들을 둘러보는 퀘스트예요. 순서 없이 각 스팟에 가서 설명과 사진을 보고, 도착하면 체크인하면 돼요.",
  "explainer.spot.howAnywhereNoLimit":
    "퀘스트를 열고 걷거나 자전거 등으로 스팟에 방문하세요. 도착하면 스팟을 표시하면 돼요. 순서는 정해져 있지 않고, 제한 시간도 없어요.",
  "explainer.spot.howAnywhereLimit":
    "퀘스트를 열고 걷거나 자전거 등으로 스팟에 방문하세요. 도착하면 스팟을 표시하면 돼요. 순서는 정해져 있지 않고, 이 퀘스트는 {count}분 제한이 있어요.",
  "explainer.spot.howFixedNoLimit":
    "표시된 시작 스팟에서 출발한 뒤, 다른 스팟은 원하는 순서로 방문하세요. 도착할 때마다 스팟을 표시하면 돼요. 제한 시간 없이 자신의 페이스로 즐기면 됩니다.",
  "explainer.spot.howFixedLimit":
    "표시된 시작 스팟에서 출발한 뒤, 다른 스팟은 원하는 순서로 방문하세요. 도착할 때마다 스팟을 표시하면 돼요. 이 퀘스트는 {count}분 제한이 있어요.",
  "explainer.spot.tips":
    "안전과 교통법규를 지키고, 주민·상점·사유지를 존중해 주세요. 필요할 때는 언제든 멈췄다가 이어서 진행해도 됩니다.",
  "summary.placeRoute": "{place} 일대를 도는 {distance} 루트 퀘스트예요.",
  "summary.titleRoute": "{title} — {distance} 루트 퀘스트예요.",
  "summary.placeSpots": "{place} 일대 {count}곳을 도는 스팟 퀘스트예요.",
  "summary.titleSpots": "{title} — {count}곳 스팟 퀘스트예요.",
  "summary.spotTime": "스팟에서 보내는 시간은 총 약 {time}예요.",
  "summary.walk": "걷기로 약 {time} 걸려요.",
  "summary.endpoints": "{start}에서 {finish}까지 이어져요.",
  "summary.byAuthor": "만든 사람은 {name}예요.",
  "summary.published": "{date}에 공개됐어요.",
  "summary.untitled": "제목 없음",
  "home.description":
    "Mapus는 지도 위 루트 퀘스트를 찾고, 그림처럼 그려진 경로를 걷거나 달리거나 자전거로 따라가며 GPS로 맞춰 가는 서비스예요.",
  "home.intro":
    "지도에서 퀘스트를 둘러보고, 경로와 지역을 확인한 뒤 실제 거리에서 선을 따라가 보세요. 만든 사람은 루트 퀘스트를 공개하고, 플레이어는 이동 기록으로 경로를 따라 채워요.",
};

const catalogs: Record<SeoLocale, Record<SeoMessageKey, string>> = { en, ko };

export function seoMessage(lang: SeoLocale, key: SeoMessageKey, params?: Record<string, string | number>): string {
  const template = catalogs[lang][key] ?? catalogs.en[key];
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (token, name: string) => {
    const value = params[name];
    return value == null ? token : String(value);
  });
}

export function matchSeoLocale(tag: string | null | undefined): SeoLocale | null {
  if (!tag) return null;
  const primary = tag.split(",")[0]?.trim().toLowerCase();
  if (!primary) return null;
  if (primary.startsWith("ko")) return "ko";
  if (primary.startsWith("en")) return "en";
  return null;
}

export function resolveSeoLocale(url: URL, acceptLanguage: string | null): SeoLocale {
  const query = url.searchParams.get("lang");
  if (query === "ko" || query === "en") return query;
  return matchSeoLocale(acceptLanguage) ?? "en";
}
